import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import {
    getAuth,
    setPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
    inMemoryPersistence,
    connectAuthEmulator,
    signInWithCustomToken
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { STORAGE_BUCKET } from './config/env';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, CustomProvider } from 'firebase/app-check';

// NOTE: A persistent IndexedDB cache (persistentLocalCache + multi-tab manager)
// was trialed here but REVERTED — it stalled cold Firestore reads badly (a
// single shipment-detail's reads took ~40s), because non-primary tabs proxy
// through a primary tab under the multi-tab manager and IndexedDB init on a
// large cache is expensive. Firestore's default in-memory cache still dedupes
// reads within a session, which is all the app relied on.

// Your web app's Firebase configuration
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    // Force correct storage bucket (appspot -> firebasestorage.app); see config/env.js
    storageBucket: STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Log the storage bucket being used
console.log('🔥 Firebase Storage Bucket:', firebaseConfig.storageBucket);

// Initialize Firebase - check if already initialized to prevent duplicate-app error
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
//console.log("Firebase Client Config In Use:", JSON.stringify(firebaseConfig, null, 2));
//console.log("FIREBASE ENV TEST: firebaseConfig:", firebaseConfig);

// Initialize Auth
const auth = getAuth(app);

// Explicitly pin auth session persistence. Firebase's default probes IndexedDB
// first, which fails to initialize in some private-mode / embedded webviews and
// can leave sessions silently non-persistent (a cause of "sign-in doesn't stick").
// Force localStorage-backed persistence, degrading gracefully to session-only and
// finally in-memory so sign-in still works even where storage is locked down.
setPersistence(auth, browserLocalPersistence)
    .catch(() => setPersistence(auth, browserSessionPersistence))
    .catch(() => setPersistence(auth, inMemoryPersistence))
    .catch((err) => console.warn('[Firebase] Could not set any auth persistence:', err?.code || err));

// Initialize Firestore - Default database (in-memory cache)
const db = getFirestore(app);

// Initialize Storage
const storage = getStorage(app);

// Initialize Functions
const functions = getFunctions(app);

// Define callable functions
const analyzeRatesWithAI = httpsCallable(functions, 'analyzeRatesWithAI');
const getMapsApiKey = httpsCallable(functions, 'getMapsApiKey');

// Helper function to wrap callable functions
const wrapCallable = (func) => {
    return async (data) => {
        try {
            const result = await func(data);
            return result.data; // Assuming functions return { data: ... }
        } catch (error) {
            console.error(`Error calling function ${func.name}:`, error);
            throw error; // Re-throw for components to handle
        }
    };
};

// Define wrapped functions
const wrappedAnalyzeRatesWithAI = wrapCallable(analyzeRatesWithAI);
const wrappedGetMapsApiKey = wrapCallable(getMapsApiKey);

// ─────────────────────────────────────────────────────────────────────────────
// APP CHECK (the public self-serve callables)
// ─────────────────────────────────────────────────────────────────────────────
// requestSelfServeAccount and completeSelfServeAccount need no sign-in, so they
// enforce App Check (reCAPTCHA Enterprise) and consume limited-use tokens. App
// Check starts only when a sign-up page asks for it (ensureAppCheck), so the
// rest of the app does not load reCAPTCHA or exchange tokens on every page.
// The sign-up pages call it on mount, which gives reCAPTCHA time to score the
// visit before the form is sent.
//
// Without a site key (REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY) no provider is
// set up and the callables are sent without a token: the server refuses them
// (fail closed), and the page shows its "could not verify this browser" error.
let appCheck; // undefined: not started yet; null: unavailable
let emulatorAppCheckProvider = null; // set by the emulator block below only

const ensureAppCheck = () => {
    if (appCheck !== undefined) return appCheck;
    const siteKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY;
    const provider = emulatorAppCheckProvider || (siteKey ? new ReCaptchaEnterpriseProvider(siteKey) : null);
    if (!provider) {
        appCheck = null;
        return appCheck;
    }
    try {
        appCheck = initializeAppCheck(app, { provider, isTokenAutoRefreshEnabled: false });
    } catch (err) {
        console.warn('[Firebase] App Check could not start:', err?.code || err?.message || err);
        appCheck = null;
    }
    return appCheck;
};

// ─────────────────────────────────────────────────────────────────────────────
// LOCAL EMULATOR MODE (development only)
// ─────────────────────────────────────────────────────────────────────────────
// Admin pages are behind auth, so there was no way to exercise them on
// localhost — every UI change had to be verified by deploying to production
// first. Pointing the SDK at the Firebase emulators gives a real, signed-in
// local session against seeded data, with no production credentials involved.
//
// Enabled only when BOTH hold, so a production bundle can never reach this:
//   * NODE_ENV === 'development'  (webpack inlines this; the branch is then
//     dead code in a prod build and is dropped by the minifier)
//   * REACT_APP_USE_EMULATORS === 'true'  (opt-in, set in .env.local)
//
// Start it with:  npm run dev:emulators   (see scripts/dev-seed-emulator.js)
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATORS === 'true') {
    const H = process.env.REACT_APP_EMULATOR_HOST || '127.0.0.1';
    try {
        connectAuthEmulator(auth, `http://${H}:9099`, { disableWarnings: true });
        connectFirestoreEmulator(db, H, 8080);
        connectStorageEmulator(storage, H, 9199);
        connectFunctionsEmulator(functions, H, 5001);
        console.log('%c🧪 Firebase EMULATOR mode', 'color:#6366f1;font-weight:bold', `auth/firestore/storage/functions → ${H}`);
    } catch (err) {
        console.error('[Firebase] Failed to connect emulators:', err);
    }

    const b64 = (o) => btoa(JSON.stringify(o)).replace(/=+$/, '');
    const b64url = (o) => b64(o).replace(/\+/g, '-').replace(/\//g, '_');
    const now = Math.floor(Date.now() / 1000);

    // App Check on the emulator: the Functions emulator decodes App Check tokens
    // but never verifies them, so an UNSIGNED token naming this app is enough to
    // exercise the self-serve callables offline. Like the custom token below it
    // carries no secret, and real App Check rejects it outright.
    emulatorAppCheckProvider = new CustomProvider({
        getToken: async () => {
            const iat = Math.floor(Date.now() / 1000);
            return {
                token: [
                    b64url({ alg: 'none', typ: 'JWT' }),
                    b64url({
                        iss: 'https://firebaseappcheck.googleapis.com/emulator',
                        sub: firebaseConfig.appId || 'emulator-app',
                        aud: [`projects/${firebaseConfig.projectId}`],
                        iat,
                        exp: iat + 3600
                    }),
                    ''
                ].join('.'),
                expireTimeMillis: (iat + 3600) * 1000
            };
        }
    });

    // Auto sign-in as the seeded developer account. The Auth emulator does not
    // verify custom-token signatures, so this is an UNSIGNED token built in the
    // browser — it carries no secret and is rejected outright by real Firebase
    // Auth. The uid must match the one scripts/dev-seed-emulator.js creates.
    // REACT_APP_DEV_AUTO_SIGNIN=false turns it off, to walk the signed-out pages
    // (sign-in, sign-up, activation) against the emulators.
    const autoSignIn = process.env.REACT_APP_DEV_AUTO_SIGNIN !== 'false';
    const devUid = process.env.REACT_APP_DEV_UID || 'dev-superadmin';
    const unsignedCustomToken = [
        b64({ alg: 'none', typ: 'JWT' }),
        b64({
            iss: 'firebase-auth-emulator@example.com',
            sub: 'firebase-auth-emulator@example.com',
            aud: 'https://identitytoolkit.googleapis.com/google.identity.identitytoolkit.v1.IdentityToolkit',
            iat: now,
            exp: now + 3600,
            uid: devUid
        }),
        ''
    ].join('.');

    if (autoSignIn) {
        auth.onAuthStateChanged((u) => {
            if (u) return;
            signInWithCustomToken(auth, unsignedCustomToken)
                .then((c) => console.log('%c🧪 Signed in as dev user', 'color:#6366f1', c.user.uid))
                .catch((e) => console.error('[Firebase] Emulator auto sign-in failed — did you run the seed script?', e?.code || e));
        });
    } else {
        console.log('%c🧪 Emulator auto sign-in is off (REACT_APP_DEV_AUTO_SIGNIN=false)', 'color:#6366f1');
    }
}

export {
    auth,
    db,
    storage,
    functions,
    ensureAppCheck,
    wrappedAnalyzeRatesWithAI,
    wrappedGetMapsApiKey
}; 